import { useSetAtom } from 'jotai'
import { ReactNode, useEffect, useMemo, useState } from 'react'

import { BalancesRpcCallUpdater } from '@cowprotocol/balances-and-allowances'
import type { SupportedChainId } from '@cowprotocol/cow-sdk'
import { LP_TOKEN_LIST_CATEGORIES, useAllLpTokens } from '@cowprotocol/tokens'

import ms from 'ms.macro'

import { areLpBalancesLoadedAtom } from './lpBalancesState'

const LP_BALANCES_QUERY_OPTIONS = { refetchInterval: 32_000, refetchOnMount: false } as const

// To avoid high load to the node at the same time
// We start the updater with a delay
const LP_UPDATER_START_DELAY = ms`3s`

export interface BalancesAndAllowancesUpdaterProps {
  account: string | undefined
  chainId: SupportedChainId
  enablePolling: boolean
}

export function LpBalancesAndAllowancesUpdater({
  account,
  chainId,
  enablePolling,
}: BalancesAndAllowancesUpdaterProps): ReactNode {
  const allLpTokens = useAllLpTokens(LP_TOKEN_LIST_CATEGORIES)
  const [isUpdaterPaused, setIsUpdaterPaused] = useState(true)
  const setAreLpBalancesLoaded = useSetAtom(areLpBalancesLoadedAtom)

  const lpTokenAddresses = useMemo(() => allLpTokens.map((token) => token.address), [allLpTokens])

  useEffect(() => {
    if (!enablePolling) return

    const timeout = setTimeout(() => {
      setIsUpdaterPaused(false)
    }, LP_UPDATER_START_DELAY)

    return () => clearTimeout(timeout)
  }, [enablePolling])

  useEffect(() => {
    setAreLpBalancesLoaded(false)
  }, [account, setAreLpBalancesLoaded])

  const queryOptions = enablePolling ? LP_BALANCES_QUERY_OPTIONS : undefined

  return (
    <>
      <BalancesRpcCallUpdater
        chainId={chainId}
        tokenAddresses={lpTokenAddresses}
        account={isUpdaterPaused ? undefined : account}
        setLoadingState={false}
        onBalancesLoaded={setAreLpBalancesLoaded}
        query={queryOptions}
      />
    </>
  )
}
